import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { shopifyClient } from "@/lib/shopify";
import { useModalParam } from "@/lib/url-modal";

type AnyCheckout = {
  id: string;
  webUrl: string;
  lineItems: Array<{
    id: string;
    title: string;
    quantity: number;
    variant: {
      id: string;
      title: string;
      price: { amount: string; currencyCode: string };
      image?: { src: string } | null;
    } | null;
  }>;
  subtotalPrice: { amount: string; currencyCode: string };
  totalPrice: { amount: string; currencyCode: string };
};

type CartContextValue = {
  checkout: AnyCheckout | null;
  loading: boolean;
  itemCount: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (variantId: string, quantity?: number) => Promise<void>;
  updateItem: (lineItemId: string, quantity: number) => Promise<void>;
  removeItem: (lineItemId: string) => Promise<void>;
};

const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = "rp_shopify_checkout_id";

export function CartProvider({ children }: { children: ReactNode }) {
  const [checkout, setCheckout] = useState<AnyCheckout | null>(null);
  const [loading, setLoading] = useState(true);
  // Drawer visibility lives in the URL (?modal=cart) so the open cart is
  // deep-linkable and the back button closes it.
  const [isOpen, setIsOpen] = useModalParam("cart");
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const init = async () => {
      try {
        const existingId =
          typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;

        if (existingId) {
          const existing = (await shopifyClient.checkout.fetch(
            existingId,
          )) as unknown as AnyCheckout | null;
          if (existing && !(existing as unknown as { completedAt?: string }).completedAt) {
            setCheckout(existing);
            setLoading(false);
            return;
          }
        }

        const fresh = (await shopifyClient.checkout.create()) as unknown as AnyCheckout;
        localStorage.setItem(STORAGE_KEY, fresh.id);
        setCheckout(fresh);
      } catch (e) {
        console.error("[cart] init failed", e);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const ensureCheckout = async (): Promise<AnyCheckout> => {
    if (checkout) return checkout;
    const fresh = (await shopifyClient.checkout.create()) as unknown as AnyCheckout;
    localStorage.setItem(STORAGE_KEY, fresh.id);
    setCheckout(fresh);
    return fresh;
  };

  const addItem: CartContextValue["addItem"] = async (variantId, quantity = 1) => {
    const co = await ensureCheckout();
    const updated = (await shopifyClient.checkout.addLineItems(co.id, [
      { variantId, quantity },
    ])) as unknown as AnyCheckout;
    setCheckout(updated);
    setIsOpen(true);
  };

  const updateItem: CartContextValue["updateItem"] = async (lineItemId, quantity) => {
    if (!checkout) return;
    const updated = (await shopifyClient.checkout.updateLineItems(checkout.id, [
      { id: lineItemId, quantity },
    ])) as unknown as AnyCheckout;
    setCheckout(updated);
  };

  const removeItem: CartContextValue["removeItem"] = async (lineItemId) => {
    if (!checkout) return;
    const updated = (await shopifyClient.checkout.removeLineItems(checkout.id, [
      lineItemId,
    ])) as unknown as AnyCheckout;
    setCheckout(updated);
  };

  const itemCount = useMemo(
    () => checkout?.lineItems.reduce((sum, li) => sum + li.quantity, 0) ?? 0,
    [checkout],
  );

  const value: CartContextValue = {
    checkout,
    loading,
    itemCount,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    addItem,
    updateItem,
    removeItem,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
